import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { fetchProductDetails } from "../../redux/features/productSlice";
import { fetchProductReviews } from "../../redux/features/productReviewSlice";
import { useDispatch, useSelector } from "react-redux";
import { addToCart, updateCart } from "../../redux/features/cartSlice";
import { addToWhishlist } from "../../redux/features/addToWhishlistSlice";
import { removeWishItem } from "../../redux/features/getWishlistSlice";
import { toast } from "react-toastify";
import RelatedProducts from "./productsNewDesign/RelatedProducts";
import ProductGallery from "./ProductGallery";
import { useTranslation } from "react-i18next";
import { useModal } from "../../hooks/ModalContext";

function ShopDetails() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [nav1, setNav1] = useState(null);
  const [nav2, setNav2] = useState(null);
  const { shopId } = useParams();
  const [quantity, setQuantity] = useState(1);
  const [loading, setLoading] = useState(true);

  const dispatch = useDispatch();
  const { product } = useSelector((state) => state.product);
  const [isWishlisted, setIsWishlisted] = useState(product?.in_whishlist);
  const { api_token } = useSelector((state) => state.auth);
  const lang = useSelector((state) => state.language.lang);

  const { activeModal, setActiveModal, emailForOtp, setEmailForOtp } = useModal();

  const { emailForOtp: emailForOtpLogin } = useSelector((state) => state.auth);
  const { emailForOtp: emailForOtpRegister } = useSelector(
    (state) => state.register
  );

  const { reviews } = useSelector((state) => state.reviews);
  //console.log(review);
  useEffect(() => {
    if (shopId) {
      setLoading(true); // Start loading

      dispatch(fetchProductDetails({ shopId, lang }))
        .then(() => {
          if (api_token) {
            return dispatch(
              fetchProductReviews({
                last_id: "",
                limit: 10,
                product_id: shopId,
                api_token,
              })
            );
          }
        })
        .finally(() => {
          setLoading(false); // Stop loading after fetching data
        });
    }
  }, [dispatch, shopId, api_token, lang]);

  function timeAgo(timestamp) {
    const currentTime = new Date();
    const timeDifference = currentTime - new Date(timestamp * 1000); // Convert timestamp to milliseconds

    const seconds = Math.floor(timeDifference / 1000);
    const minutes = Math.floor(seconds / 60);
    const hours = Math.floor(minutes / 60);
    const days = Math.floor(hours / 24);
    const months = Math.floor(days / 30);
    const years = Math.floor(months / 12);

    if (years > 0) {
      return years === 1 ? "1 year ago" : `${years} years ago`;
    } else if (months > 0) {
      return months === 1 ? "1 month ago" : `${months} months ago`;
    } else if (days > 0) {
      return days === 1 ? "1 day ago" : `${days} days ago`;
    } else if (hours > 0) {
      return hours === 1 ? "1 hour ago" : `${hours} hours ago`;
    } else if (minutes > 0) {
      return minutes === 1 ? "1 minute ago" : `${minutes} minutes ago`;
    } else {
      return seconds === 1 ? "1 second ago" : `${seconds} seconds ago`;
    }
  }

  const settingsMain = {
    dots: false,
    arrows: false,
    slidesToShow: 1,
    slidesToScroll: 1,
    fade: true,
    asNavFor: nav2,
    autoplay: true,
    autoplaySpeed: 7000,
  };

  const settingsThumbnail = {
    dots: false,
    arrows: false,
    slidesToShow: 4,
    slidesToScroll: 1,
    focusOnSelect: true,
    asNavFor: nav1,
    vertical: false,
    verticalSwiping: false,

    // 🔹 Change orientation based on screen width
    responsive: [
      {
        breakpoint: 1100, // Below xl (large devices)
        settings: {
          vertical: false,
          verticalSwiping: false,
          slidesToShow: 4,
        },
      }
    ],
  };

  const [selectedVariants, setSelectedVariants] = useState({});

  const handleVariantChange = (key, value) => {
    setSelectedVariants((prev) => ({ ...prev, [key]: value }));
  };

  const [selectedBigBuyVariant, setSelectedBigBuyVariant] = useState(null);

  const handleBigBuyVariantChange = (attributeName, variantId) => {
    const selected = product?.variants?.find(
      (variant) => variant.variant_id === variantId
    );
    setSelectedBigBuyVariant(selected);
  };

  const handleAddToCart = () => {
    if (!api_token) {
      toast.error(`${t("You_need_to_login_to_add_product_to_your_cart")}`, {
        hideProgressBar: true,
        position: 'bottom-right',
        theme: 'dark',

      });
      setActiveModal("login");
      return;
    }

    // 🔹 Check if product has variants
    if (!product.bigbuy_product) {
      if (Object.keys(selectedVariants).length !== product.variations.length) {
        toast.error(`${t("Please_select_variant_before_adding_to_cart")}.`, {
          position: "top-right",
          hideProgressBar: true,
          position: 'bottom-right',
          theme: 'dark',

          autoClose: 2000,
        });
        return;
      }
    }

    let combinations = [];

    // 🔹 Create variant combinations
    if (!product.bigbuy_product) {
      combinations =
        product.variations.length > 0
          ? Object.entries(selectedVariants).map(([key, value]) => ({
            key,
            title: value,
            value,
          }))
          : [];
    } else if (selectedBigBuyVariant) {
      combinations = selectedBigBuyVariant;
    }

    // 🔹 Dispatch add to cart
    dispatch(addToCart({ product_id: shopId, combinations }));

    // ✅ Show success message
    toast.success(`${t("Product added to your cart!")}`, {
      position: "top-right",
      hideProgressBar: true,
      position: 'bottom-right',
      theme: 'dark',

      autoClose: 2000,
    });

    // Optional: navigate after delay for better UX
    setTimeout(() => navigate("/cart", { replace: true }), 500);
  };

  const handleAddToWishlist = (productId) => {
    if (!api_token) {
      toast.error(`${t("You_need_to_login_to_add_product_to_your_wishlist")}`, {
        hideProgressBar: true,
        position: 'bottom-right',
        theme: 'dark',

      });
      setActiveModal("login");
      return;
    }

    setIsWishlisted(true);
    dispatch(addToWhishlist({ product_id: productId }));

    // ✅ Success feedback
    toast.success(`${t("Added_to_your_wishlist")} ❤️`, {
      position: "top-right",
      hideProgressBar: true,
      position: 'bottom-right',
      theme: 'dark',

      autoClose: 1500,
    });
  };

  const handleRemoveFromWishlist = (productId) => {
    if (!api_token) {
      toast.error(`${t("You_need_to_login_to_remove_product_from_your_wishlist")}`, {
        hideProgressBar: true,
        position: 'bottom-right',
        theme: 'dark',

      });
      setActiveModal("login");
      return;
    }

    setIsWishlisted(false);
    dispatch(removeWishItem(productId));

    // ✅ Success feedback
    toast.success(`${t("Removed_from_your_wishlist")} 💔`, {
      position: "top-right",
      hideProgressBar: true,
      position: 'bottom-right',
      theme: 'dark',

      autoClose: 1500,
    });
  };

  const handleUpdateCart = async (product_id, newQuantity) => {
    if (newQuantity < 1) {
      toast.error(`${t("Quantity_must_be_at_least_1.")}`, {
        position: "top-right", hideProgressBar: true,
        position: 'bottom-right',
        theme: 'dark',
        autoClose: 2000
      });
      return;
    }

    try {
      const resultAction = await dispatch(updateCart({ product_id, quantity: newQuantity }));

      if (updateCart.fulfilled.match(resultAction)) {
        toast.success(`${t("Cart_updated_successfully")} 🛒`, {
          position: "top-right",
          hideProgressBar: true,
          position: 'bottom-right',
          theme: 'dark',

          autoClose: 2000,
        });
      } else {
        throw new Error(resultAction.error?.message || `${t("Failed_to_update_cart")}`);
      }
    } catch (error) {
      toast.error(error.message || `${t("Error_updating_cart")}.`, {
        position: "top-right",
        hideProgressBar: true,
        position: 'bottom-right',
        theme: 'dark',

        autoClose: 2000,
      });
    }
  };

  useEffect(() => {
    window.scrollTo({
      top: 0,
      behavior: "smooth",
    });
  }, [shopId]);


  if (loading) {
    return (
      <div className="loading-container">
        <div className="spinner"></div>
        <p>{t("Loading_product_details")}...</p>
      </div>
    );
  }

  return (
    <>
      <section className="shop-details-section pt-4 pb-80 main-bg">
        <div className="container">
          <div className="shop-details-wrapper">
            <div className="row">
              <div className="col-xl-6">
                <ProductGallery product={product} />
              </div>
              <div className="col-xl-6 px-4">
                <div
                  className="product-info mb-50 aos-init aos-animate"
                  data-aos="fade-up"
                  data-aos-duration="1400"
                >
                  {/* <span className="sale">
                    <i className="fas fa-tags"></i>SALE 70% OFF
                  </span> */}
                  <h4 className="title">{product?.title}</h4>
                  {/* <ul className="ratings rating5">
                    <li>
                      <i className="fas fa-star"></i>
                    </li>
                    <li>
                      <i className="fas fa-star"></i>
                    </li>
                    <li>
                      <i className="fas fa-star"></i>
                    </li>
                    <li>
                      <i className="fas fa-star"></i>
                    </li>
                    <li>
                      <i className="fas fa-star"></i>
                    </li>
                    <li>
                      <a href="#">(45 Reviews)</a>
                    </li>
                  </ul> */}
                  {/* <div
                    dangerouslySetInnerHTML={{ __html: product?.description }}
                  /> */}

                  <div className="product-price">
                    <span className="price prev-price">
                      <span className="currency">€</span>
                      {selectedBigBuyVariant
                        ? selectedBigBuyVariant.in_shops_price
                        : product?.in_shop_price}
                    </span>
                    <span className="price new-price">
                      <span className="currency">€</span>
                      {selectedBigBuyVariant
                        ? selectedBigBuyVariant.retail_price
                        : product?.price}
                    </span>
                  </div>

                  {!product?.bigbuy_product ? (
                    product?.variations?.map((variation, index) => (
                      <div key={index} className="product-size">
                        <h4 className="mb-3">{variation.title}</h4>
                        <ul className="size-list mb-30">
                          {variation.value.map((option, i) => (
                            <li key={i}>
                              <div className="form-check">
                                <input
                                  className="form-check-input"
                                  type="radio"
                                  name={variation.key}
                                  value={option.key}
                                  id={`${variation.key}-${i}`}
                                  onChange={() =>
                                    handleVariantChange(
                                      variation.key,
                                      option.key
                                    )
                                  }
                                />
                                <label
                                  className="form-check-label"
                                  htmlFor={`${variation.key}-${i}`}
                                >
                                  {option.title}
                                </label>
                              </div>
                            </li>
                          ))}
                        </ul>
                      </div>
                    ))
                  ) : (
                    <div className="product-size">
                      <ul className="size-list mb-30">
                        {product?.variants?.map((variation, index) => (
                          <li key={index}>
                            <div className="form-check">
                              <input
                                className="form-check-input"
                                type="radio"
                                name={variation.product_id}
                                value={variation.variant_id}
                                id={`${variation.product_id}-${index}`}
                                onChange={() =>
                                  handleBigBuyVariantChange(
                                    variation.attribute_name,
                                    variation.variant_id
                                  )
                                }
                              />
                              <label
                                className="form-check-label"
                                htmlFor={`${variation.product_id}-${index}`}
                              >
                                {variation.attribute_name}
                              </label>
                            </div>
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}

                  <div className="product-cart-variation mt-3">
                    <ul>
                      <li>
                        {product?.in_cart ? (
                          <button
                            className="theme-btn style-one"
                            onClick={() =>
                              handleUpdateCart(product.id, product.quantity + 1)
                            }
                          >
                            <i className="fa fa-plus me-2"></i>{" "}
                            <span className="text">{t("add_more")}</span>
                          </button>
                        ) : (
                          <button
                            onClick={() => handleAddToCart()}
                            className="theme-btn style-one"
                          >
                            {t("add_to_cart")}
                          </button>
                        )}
                      </li>
                      <li>
                        <button
                          onClick={() =>
                            isWishlisted || product?.in_whishlist
                              ? handleRemoveFromWishlist(product.id)
                              : handleAddToWishlist(product.id)
                          }
                          className="icon-btn"
                        >
                          <i
                            className={`fas fa-heart ${isWishlisted || product?.in_whishlist
                              ? "text-danger"
                              : ""
                              }`}
                          ></i>
                        </button>
                      </li>
                    </ul>
                  </div>
                  <div className="product-meta">
                    <div>
                      <b>{t("Estimated_Delivery_on")}: </b>{" "}
                      {new Date(
                        Date.now() + (product?.delivered_day || 2) * 86400000
                      ).toLocaleDateString("en-US", {
                        weekday: "long",
                        day: "2-digit",
                        month: "long",
                        year: "numeric",
                      })}
                    </div>
                  </div>
                  <div className="special-features">
                    <span className="mb-2 mb-sm-0">
                      <i className="fa fa-shipping-fast"></i>{t("Free_Shipping")}
                    </span>
                    <span>
                      <i className="fa fa-box-open"></i>{t("Easy_Returns")}
                    </span>
                  </div>
                </div>
              </div>
            </div>
            <div
              className="additional-information-wrapper aos-init"
              data-aos="fade-up"
              data-aos-delay="30"
              data-aos-duration="1000"
            >
              <div className="row">
                <div className="col-lg-12">
                  <div className="description-wrapper mb-40">
                    <div className="pesco-tabs style-two mb-50">
                      <ul className="nav nav-tabs" role="tablist">
                        <li>
                          <button
                            className="nav-link active"
                            data-bs-toggle="tab"
                            data-bs-target="#description"
                            fdprocessedid="gsdjnk"
                            aria-selected="true"
                            role="tab"
                          >
                            {t("Description")}
                          </button>
                        </li>
                        <li>
                          <button
                            className="nav-link"
                            data-bs-toggle="tab"
                            data-bs-target="#reviews"
                            fdprocessedid="r255ub"
                            aria-selected="false"
                            tabindex="-1"
                            role="tab"
                          >
                            {t("Reviews")}
                          </button>
                        </li>
                      </ul>
                    </div>
                    <div className="tab-content">
                      <div
                        className="tab-pane fade active show pt-4"
                        id="description"
                        role="tabpanel"
                      >
                        <h4>{t("Description")}</h4>
                        <div
                          dangerouslySetInnerHTML={{
                            __html: product?.description,
                          }}
                          className="bg-white p-3"
                        />
                      </div>
                      <div
                        className="tab-pane fade pt-4"
                        id="reviews"
                        role="tabpanel"
                      >
                        <div className="pesco-comment-area mb-80">
                          <h4>{t("Total_Reviews")} ({reviews?.total})</h4>
                          <ul className="row">
                            {reviews?.records.map((review, index) => (
                              <li key={index} className="col-md-4 mb-3">
                                <div className="comment pesco-reviews-item h-100">
                                  <div className="author-thumb-info">
                                    <div className="author-thumb">
                                      <img
                                        src={review.user_image}
                                        alt="Author"
                                      />
                                    </div>
                                    <div className="author-info">
                                      <h5>{review.user_name}</h5>
                                      <div className="author-meta">
                                        <ul className="ratings">
                                          {[...Array(review.rating)].map(
                                            (_, idx) => (
                                              <li key={idx}>
                                                <i className="fas fa-star"></i>
                                              </li>
                                            )
                                          )}
                                        </ul>
                                        <span>
                                          {timeAgo(review.created_date)}
                                        </span>
                                      </div>
                                    </div>
                                  </div>
                                  <div className="author-review-content">
                                    <p>{review.comments}</p>
                                  </div>
                                </div>
                              </li>
                            ))}
                          </ul>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <RelatedProducts category_id={product?.category_id} />
      </section>
    </>
  );
}
export default ShopDetails;
